import React, {useEffect, useRef, useState} from 'react';

import {UserLink} from 'src/components/links';
import {useAdminAccessRight} from 'src/hooks/access-rights';

import {Drawer, DrawerHeader, DrawerHole, DrawerMain} from '@pb/kit-component-drawer';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Skeleton, SkeletonItem} from '@pb/kit-component-skeleton';
import foundations from '@pb/kit-styles-primitives/foundations.json';

import {DrawerAccessSummary, DrawerLoadError} from './drawer-access-summary';
import {EditAccessButton} from './manage-seats-dialog';
import {useUserDrawerDetails} from './use-user-drawer-details';

import {drawerContent, emailText, fieldsGrid, userDrawer} from './user-details-drawer.module.styl';

import type {UserDetails} from 'src/openapi/admin/models/UserDetails';


type Props = {
    userId: number,
    open: boolean,
    onClose: () => void,
    canViewProducts: boolean,
    canViewLicense: boolean,
    onAccessChanged?: (details: UserDetails) => void,
};

export const UserDetailsDrawer = ({userId, open, onClose, canViewProducts, canViewLicense, onAccessChanged}: Props) => {
    const {data, error, retry, updateDetails} = useUserDrawerDetails(userId, open);
    const rights = useAdminAccessRight();
    const accountId = data?.profile.account?.accountId;
    const canAccessProducts = canViewProducts && accountId !== undefined &&
        (rights.canViewUserProducts(accountId) || rights.canEditUserProducts(accountId));
    const [accessVersion, setAccessVersion] = useState(0);
    const tabsRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        tabsRef.current = document.querySelector('.tabs');
    }, []);
    const title = data ? `${data.details.firstName} ${data.details.lastName}`.trim() || 'User Details' : 'User Details';
    const email = data?.details.email || '';
    const fields = data ? [
        ['First Name', data.details.firstName], ['Last Name', data.details.lastName],
        ['Email Address', email], ['Job Title', data.details.jobTitle],
        ['Group', data.profile.groupName], ['PB RTS ID', data.details.pbId],
        ['PB Salesforce ID', data.integration.crmId], ['Edu Person Affiliation', data.login.educationAttribute],
    ] : [];


    return (
        <Drawer
            open={open}
            setOpen={shown => {
                if (!shown) {
                    onClose();
                }
            }}
            placement="right"
            aria-label={title}
            stickToElement={tabsRef.current || undefined}
            className={userDrawer}
            style={{'width': 'min(50rem, 100vw)', '--drawer-border': foundations.core.border['border-default'],
                '--drawer-success': foundations.component.indicators['indicator-positive']} as React.CSSProperties}>
            <DrawerHeader subtitle={data ? (
                <Group gap="m">
                    <span className={emailText} title={email}>
                        <UserLink userId={userId} place="userDetailsDrawer" caption={email}/>
                    </span>
                    <span>{data.profile.account?.accountName || '—'}</span>
                </Group>
            ) : undefined}>
                {title}
            </DrawerHeader>
            <DrawerMain>
                <DrawerHole>
                    <div className={`${drawerContent} enable-selection`}>
                        {error ? (
                            <DrawerLoadError
                                message={error.message || 'Unable to load user details.'}
                                retry={retry}
                            />
                        ) : (
                            <Skeleton shown={!data} view={<SkeletonItem height={240}/>}>
                                {data ? (
                                    <>
                                        <FormSection headline="User Information" borderBottom={true}>
                                            <div className={fieldsGrid}>
                                                {fields.map(([label, value]) => (
                                                    <div key={label}>
                                                        <Label>{label}</Label>
                                                        <div
                                                            className={label === 'Email Address' ? emailText : undefined}
                                                            title={label === 'Email Address' ? email : undefined}>
                                                            {value || '—'}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </FormSection>
                                        {canAccessProducts ? (
                                            <FormSection
                                                headline="Access Summary"
                                                borderBottom={true}
                                                last={true}
                                                info={headline => (
                                                    <Group justify="space-between" style={{flexGrow: 1}}>
                                                        {headline}
                                                        {data.profile.account?.accountId ? (
                                                            <EditAccessButton
                                                                userId={userId}
                                                                accountId={data.profile.account.accountId}
                                                                email={data.details.email}
                                                                onChanged={details => {
                                                                    updateDetails(details);
                                                                    setAccessVersion(value => value + 1);
                                                                    onAccessChanged?.(details);
                                                                }}
                                                            />
                                                        ) : null}
                                                    </Group>
                                                )}>
                                                <DrawerAccessSummary
                                                    key={accessVersion}
                                                    userId={userId}
                                                    details={data.details}
                                                    accountId={data.profile.account?.accountId}
                                                    canViewProducts={canViewProducts}
                                                    canViewLicense={canViewLicense}
                                                />
                                            </FormSection>
                                        ) : null}
                                    </>
                                ) : null}
                            </Skeleton>
                        )}
                    </div>
                </DrawerHole>
            </DrawerMain>
        </Drawer>
    );
};
